<script>
import { toMoney, toDecPercent, formatNumber, getNumber } from '@utils/Format'
import { prependCurrencyMask } from '@modules/consolidator/utils/Currency/Currency'
import { customSort as baseCustomSort } from '@utils/Sort'
import { decimalLengthQuantity } from '@utils/Asset'
import { FORMAT_ORIGEM } from '@constants/Text'
import { light_mode } from '@constants/Color'
import EventIcon from '@modules/consolidator/components/event-icon/event-icon.vue'
import TableRowActions from '@modules/consolidator/components/table-row-actions/table-row-actions.vue'
import IaSuggestionsPendingBadge from '@modules/crm/components/ia-suggestions-pending-badge/ia-suggestions-pending-badge.vue'
import { CONSOLIDATOR_TABLE_CUSTOM_SORT_FIELDS } from '@modules/consolidator/constants/Text'
import {
  normalizeConsolidatorTableHeaders,
  buildMovementRowActions,
  buildEventRowActions,
} from '@modules/consolidator/utils/table/table'
import {
  hasZeroedValues,
  isBonusEvent,
  relabelUnconvertedValue,
  resolveEventDisplayText,
  resolveEventRowCurrency,
  resolveEventAssetCurrency,
} from '@modules/consolidator/utils/Events/Events'

export default {
  name: 'MovementsEventsCombinedTable',
  components: {
    EventIcon,
    TableRowActions,
    IaSuggestionsPendingBadge,
  },
  props: {
    asset: {
      type: Object,
      default: () => ({}),
    },
    movementItems: {
      type: Array,
      default: () => [],
    },
    // Contagem de sugestoes pendentes POR MOVIMENTACAO (`{ <id_aplicacao>: n }`). `null` = fila
    // nao apurada; a linha entao nao afirma nada, em vez de afirmar zero.
    sugestoesPorMovimentacao: {
      type: Object,
      default: null,
    },
    eventItems: {
      type: Array,
      default: () => [],
    },
    search: {
      type: String,
      default: '',
    },
    sortBy: {
      type: Array,
      default: () => ['data_cotizacao'],
    },
    sortDesc: {
      type: Array,
      default: () => [true],
    },
    showAlertFillPu: {
      type: Boolean,
      default: false,
    },
    idAssetAlertFillPu: {
      type: [Number, String],
      default: null,
    },
    showAssetNameColumn: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formatNumber,
      getNumber,
      FORMAT_ORIGEM,
      warningColor: light_mode['yellow-dark'],
    }
  },
  computed: {
    // Mapa em vez de funcao no template: o template reavalia a funcao a cada re-render, e a
    // tabela do Extrato tem centenas de linhas. Mesmo padrao do `sugestoesPendentesById`.
    sugestoesPorLinha() {
      return this.sugestoesPorMovimentacao || {}
    },
    headers() {
      const baseHeaders = []
      const hasAsset = this.asset && this.asset.id
      const shouldIncludeAssetColumn = !hasAsset || this.showAssetNameColumn

      if (shouldIncludeAssetColumn) {
        baseHeaders.push({ text: 'Ativo', value: 'nome_ativo' })
      }

      baseHeaders.push(
        { text: 'Tipo de ordem', value: 'operacao' },
        { text: 'Data competência', value: 'data' },
        { text: 'Data cotização', value: 'data_cotizacao' },
        { text: 'Quantidade', value: 'quantidade' },
        { text: 'Preço Unitário', value: 'valor_unitario' },
        { text: 'Valor Total', value: 'valor' },
        { text: 'Data registro', value: 'data_registro' },
        { text: 'Origem', value: 'origem', default: 'Manual' }
      )

      return normalizeConsolidatorTableHeaders({
        headers: baseHeaders,
        includeActions: !this.readOnly,
      })
    },
    combinedItems() {
      const movementList = Array.isArray(this.movementItems)
        ? this.movementItems
        : []

      // Moeda e texto resolvidos POR LINHA aqui, nao no template: o `CLAUDE.md`
      // do repo proibe chamada de funcao em interpolacao (reexecuta a cada
      // re-render, e a tabela combina movimentacoes com eventos).
      const eventList = Array.isArray(this.eventItems)
        ? this.eventItems
            .filter((eventItem) => !hasZeroedValues(eventItem))
            .map((eventItem) => {
              const normalized = this.normalizeEvent({
                originalEvent: eventItem,
              })
              const moeda = this.proventoCurrency(normalized)
              const ehBonificacao = isBonusEvent(normalized)
              return {
                ...normalized,
                _valorUnitarioText: ehBonificacao
                  ? toDecPercent(normalized.valor_unitario)
                  : this.proventoUnitText(normalized, moeda),
                _valorText: this.proventoTotalText(normalized, moeda),
              }
            })
        : []

      return [...movementList, ...eventList]
    },
  },
  methods: {
    customSort(items, index, isDec) {
      return baseCustomSort(
        items,
        index,
        isDec,
        CONSOLIDATOR_TABLE_CUSTOM_SORT_FIELDS
      )
    },
    getRowActions({ item }) {
      return this.isMovement(item)
        ? buildMovementRowActions({
            item,
            onEdit: this.onEditMovement,
            onRemove: this.onRemoveMovement,
          })
        : buildEventRowActions({
            item,
            onEdit: this.onEditEvent,
            onRemove: this.onRemoveEvent,
          })
    },
    onEditMovement(item) {
      this.$emit('edit-movement', item)
    },
    onRemoveMovement(item) {
      this.$emit('remove-movement', item)
    },
    onEditEvent(item) {
      this.$emit('edit-event', item)
    },
    onRemoveEvent(item) {
      this.$emit('remove-event', item)
    },
    quantityText({ item }) {
      if (!item) {
        return ''
      }

      if (item.quantidade_f) {
        return item.quantidade_f
      }

      if (this.isMovement(item)) {
        return item.quantidade
          ? this.formatNumber(item.quantidade, decimalLengthQuantity(item))
          : '-'
      }

      return item.quantidade || ''
    },
    unitPriceText(item) {
      return prependCurrencyMask({
        value: item && item.valor_unitario,
        currency: this.resolveCurrency(item),
      })
    },
    /**
     * Moeda de UMA linha de PROVENTO. Mesma regra da `eventos-table`: o
     * discriminador e a conversao dentro do texto que a cotizacao entregou.
     *
     * Aqui o texto original vive em `valor_unitario` — o
     * `normalizeProventoMonetaryFields` move `valor` -> `valor_unitario` e
     * `valor_total` -> `valor` antes de renderizar.
     *
     * As linhas de MOVIMENTACAO ja respeitavam a moeda do ativo (via
     * `unitPriceText`/`prependCurrencyMask`); so as de provento nao.
     */
    proventoCurrency(item) {
      return resolveEventRowCurrency({
        value: item && item.valor_unitario,
        currency: this.assetCurrency(item),
      })
    },
    assetCurrency(item) {
      return resolveEventAssetCurrency({ item, asset: this.asset })
    },
    proventoUnitText(item, moeda) {
      const textoDoBackend = resolveEventDisplayText({
        event: item,
        currency: this.assetCurrency(item),
      })
      if (textoDoBackend) {
        return textoDoBackend
      }
      const valor = relabelUnconvertedValue({
        value: item && item.valor_unitario,
        currency: this.assetCurrency(item),
      })
      return toMoney(valor, 2, moeda)
    },
    proventoTotalText(item, moeda) {
      const textoDoBackend = resolveEventDisplayText({
        event: item,
        campo: 'valor_total',
        currency: this.assetCurrency(item),
      })
      if (textoDoBackend) {
        return textoDoBackend
      }
      return toMoney(item && item.valor, 2, moeda)
    },
    resolveCurrency(item) {
      return (
        (item && item.moeda) ||
        (this.asset && this.asset.moeda) ||
        (item && item.valor) ||
        ''
      )
    },
    isMovement(item) {
      if (!item) {
        return false
      }
      return !item.provento && !item.split
    },
    isProvento(item) {
      return !!(item && item.provento)
    },
    isSplit(item) {
      return !!(item && item.split)
    },
    shouldShowUnitPriceAlert(item) {
      if (!this.showAlertFillPu) {
        return false
      }
      if (!item) {
        return false
      }
      if (item.id_ativo_cliente !== this.idAssetAlertFillPu) {
        return false
      }
      return !this.getNumber(item.valor_unitario)
    },
    normalizeEvent({ originalEvent }) {
      const normalized = { ...originalEvent }
      this.fillCompetenceDate({ eventItem: normalized })
      this.fillCotizationDate({ eventItem: normalized })
      this.normalizeProventoMonetaryFields({ eventItem: normalized })
      return normalized
    },
    fillCompetenceDate({ eventItem }) {
      if (!eventItem || eventItem.data) {
        return
      }

      eventItem.data =
        eventItem.data_com ||
        eventItem.data_pagamento ||
        eventItem.data_cotizacao
    },
    fillCotizationDate({ eventItem }) {
      if (!eventItem || eventItem.data_cotizacao) {
        return
      }

      eventItem.data_cotizacao =
        eventItem.data_pagamento || eventItem.data || eventItem.data_com
    },
    normalizeProventoMonetaryFields({ eventItem }) {
      if (!eventItem || !eventItem.provento) {
        return
      }

      if (eventItem.valor != null && !eventItem.valor_unitario) {
        eventItem.valor_unitario = eventItem.valor
      }

      if (eventItem.valor_total != null) {
        eventItem.valor = eventItem.valor_total
      }
    },
    getAssetNameText({ item }) {
      const assetName =
        (item && item.nome_ativo) || (this.asset || {}).nome_ativo
      const brokerName =
        (item && item.corretora) || (this.asset || {}).corretora

      if (assetName === 'Saldo em Conta') {
        return `${assetName} - ${brokerName || ''}`.trim()
      }

      return assetName || ''
    },
  },
}
</script>

<template>
  <div class="movements-events-combined-table">
    <BaseTable
      :title="title"
      :headers="headers"
      :items="combinedItems"
      :loading="loading"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :custom-sort="customSort"
      :search-value="search"
      disable-margin-top
    >
      <template v-slot:item.icon="{ item }">
        <EventIcon :event="item" />
      </template>

      <template
        v-if="!asset || !asset.id || showAssetNameColumn"
        v-slot:item.nome_ativo="{ item }"
      >
        <div>
          {{ getAssetNameText({ item }) }}
        </div>
      </template>

      <template v-slot:item.operacao="{ item }">
        <div v-if="isMovement(item)" class="d-flex align-items-center az-gap-small">
          {{ item.operacao }}
          <IaSuggestionsPendingBadge
            v-if="sugestoesPorLinha[item.id_aplicacao]"
            :pendentes="sugestoesPorLinha[item.id_aplicacao]"
            data-test="mov-sugestoes-ia"
            @revisar="$emit('revisar-sugestoes', item)"
          />
        </div>
        <div v-else>
          {{ item.operacao }}
          <template v-if="isSplit(item)">
            <span> de {{ item.de }}:{{ item.para }}</span>
          </template>
        </div>
      </template>

      <template v-slot:item.quantidade="{ item }">
        {{ quantityText({ item }) }}
      </template>

      <template v-slot:item.valor_unitario="{ item }">
        <template v-if="isProvento(item)">
          {{ item._valorUnitarioText }}
        </template>
        <template v-else>
          {{ unitPriceText(item) }}
        </template>
      </template>

      <template v-slot:item.valor="{ item }">
        <template v-if="isProvento(item)">
          <template v-if="['Bonificação (%)'].includes(item.operacao)">
            {{ item.valor }}x
            {{ item.nome_ativo || (asset && asset.referencia) }}
          </template>
          <template v-else>
            {{ item._valorText }}
          </template>
        </template>
        <template v-else>
          {{ item.valor }}
        </template>
      </template>

      <template v-slot:item.origem="{ item }">
        <span>
          {{ FORMAT_ORIGEM[item.origem] || item.origem || '-' }}
        </span>
      </template>

      <template v-slot:item.action="{ item }">
        <div class="d-flex justify-content-center">
          <div v-if="isMovement(item) && shouldShowUnitPriceAlert(item)">
            <v-tooltip max-width="300" left>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  <icon-svg name="warning" :color="warningColor" />
                </div>
              </template>
              <span>
                Para calcular a rentabilidade, é necessário preencher o preço
                unitário e quantidade desta movimentação.
              </span>
            </v-tooltip>
          </div>

          <TableRowActions :actions="getRowActions({ item })" />
        </div>
      </template>
    </BaseTable>
  </div>
</template>

<style lang="sass">
.movements-events-combined-table
  width: 100%
</style>
